import { MoreHorizontal, PencilLine, Plus } from 'lucide-react';
import type React from 'react';
import { useUser } from '@/core/api';
import type { BackendActivityItemSchemaSnapshot } from '@/core/api/types';
import { useIntl } from '@/core/i18n/use-intl';

export interface ActivityItem {
  id: string;
  type: 'created' | 'updated';
  timestamp: string;
  user: string | null;
  changes?: {
    field: string;
    from?: unknown;
    to?: unknown;
  }[];
  schema?: BackendActivityItemSchemaSnapshot;
}

export function ActivityIcon({ type }: { type: ActivityItem['type'] }) {
  if (type === 'created') {
    return (
      <div className="flex size-6 items-center justify-center rounded-full bg-green-500/20 text-green-500">
        <Plus className="size-3.5" />
      </div>
    );
  }
  return (
    <div className="flex size-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-500">
      <PencilLine className="size-3.5" />
    </div>
  );
}

export interface ResolvedUserProps {
  userId?: string | null;
}

export function ResolvedUser({ userId }: ResolvedUserProps) {
  const { t } = useIntl();
  const { data: user } = useUser(userId || '', { disabled: !userId });

  if (!user) {
    return <span>{t('activity.system')}</span>;
  }

  return (
    <span className="font-medium">
      {user.firstName} {user.lastName}
    </span>
  );
}

interface ActivityTimelineProps {
  activities: ActivityItem[];
  children: (activity: ActivityItem, index: number, total: number) => React.ReactNode;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

export function ActivityTimeline({
  activities,
  children,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: ActivityTimelineProps) {
  const { t } = useIntl();

  return (
    <div className="relative space-y-0">
      {activities.map((activity, index) => (
        <div key={activity.id} className="relative flex gap-3 py-3 text-sm">
          <div className="relative">
            <ActivityIcon type={activity.type} />
            {(index !== activities.length - 1 || hasNextPage) && (
              <div className="absolute left-1/2 top-6 h-full w-px -translate-x-1/2 bg-border" />
            )}
          </div>
          <div className="flex-1 mt-0.5">{children(activity, index, activities.length)}</div>
        </div>
      ))}
      {hasNextPage && (
        <div className="relative flex gap-3 py-3 text-sm">
          <div className="relative">
            <div className="flex size-6 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <MoreHorizontal className="size-3.5" />
            </div>
          </div>
          <div className="flex-1 mt-0.5">
            <button
              type="button"
              onClick={() => fetchNextPage?.()}
              disabled={isFetchingNextPage}
              className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              {isFetchingNextPage ? t('activity.loadingMore') : t('activity.loadMore')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
